import { resolveProjection } from '../../grid/projection';
import { TRIANGLE_PROJECTION } from '../../grid/triangle';
import type { GridGeometry } from '../../grid';
import type { MapDefinition } from '../../model/gamestate/GameState';

interface TileMetrics {
    centerX: number;
    centerY: number;
    spanWidth: number;
    spanHeight: number;
    originX: number;
    originY: number;
}

export interface PathTileDrawGeometry {
    drawWidth: number;
    drawHeight: number;
    boxCenterX: number;
    boxCenterY: number;
}

export interface PathTileTransformCenter {
    x: number;
    y: number;
}

export function resolvePathTileDrawGeometry(
    gridType: MapDefinition['gridType'],
    tileSize: number,
    tileMetrics: TileMetrics,
    drawScale = 1,
): PathTileDrawGeometry {
    if (gridType === 'triangle') {
        const projection = resolveProjection(tileSize, TRIANGLE_PROJECTION);
        return {
            drawWidth: projection.renderWidth,
            drawHeight: projection.renderHeight,
            boxCenterX: tileMetrics.originX + tileSize / 2,
            boxCenterY: tileMetrics.originY + tileSize / 2,
        };
    }

    const drawSize = gridType === 'hex'
        ? Math.max(tileMetrics.spanWidth, tileMetrics.spanHeight)
        : tileMetrics.spanWidth;
    return {
        drawWidth: gridType === 'hex' ? drawSize * drawScale : drawSize,
        drawHeight: gridType === 'hex' ? drawSize * drawScale : tileMetrics.spanHeight,
        boxCenterX: tileMetrics.centerX,
        boxCenterY: tileMetrics.centerY,
    };
}

export function shouldClipPathTileToGrid(gridType: MapDefinition['gridType']): boolean {
    return gridType === 'hex' || gridType === 'triangle';
}

export function resolvePathTileTransformCenter(
    gridType: MapDefinition['gridType'],
    tileMetrics: TileMetrics,
): PathTileTransformCenter | null {
    if (gridType !== 'triangle') {
        return null;
    }
    return {
        x: tileMetrics.originX + tileMetrics.centroidX,
        y: tileMetrics.originY + tileMetrics.centroidY,
    };
}

export interface DrawGridClippedPathTileImageOptions {
    context: CanvasRenderingContext2D;
    grid: GridGeometry;
    gridType: MapDefinition['gridType'];
    tileSize: number;
    tileX: number;
    tileY: number;
    boxCenterX: number;
    boxCenterY: number;
    transformCenterX?: number;
    transformCenterY?: number;
    drawWidth: number;
    drawHeight: number;
    image: CanvasImageSource;
    sourceX?: number;
    sourceY?: number;
    sourceWidth?: number;
    sourceHeight?: number;
    rotationSteps: number;
    flipHorizontal: boolean;
    flipVertical: boolean;
}

export function drawGridClippedPathTileImage(options: DrawGridClippedPathTileImageOptions): void {
    const {
        context,
        grid,
        gridType,
        tileSize,
        tileX,
        tileY,
        boxCenterX,
        boxCenterY,
        transformCenterX,
        transformCenterY,
        drawWidth,
        drawHeight,
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        rotationSteps,
        flipHorizontal,
        flipVertical,
    } = options;
    const pivotX = transformCenterX ?? boxCenterX;
    const pivotY = transformCenterY ?? boxCenterY;
    const offsetX = boxCenterX - pivotX;
    const offsetY = boxCenterY - pivotY;
    context.save();
    context.translate(pivotX, pivotY);
    if (rotationSteps !== 0) {
        context.rotate(((((rotationSteps % 12) + 12) % 12) * Math.PI) / 6);
    }
    context.scale(flipHorizontal ? -1 : 1, flipVertical ? -1 : 1);
    if (shouldClipPathTileToGrid(gridType)) {
        grid.drawTilePath(context, offsetX, offsetY, tileSize * 0.5, tileX, tileY);
        context.clip();
    }
    if (
        typeof sourceX === 'number'
        && typeof sourceY === 'number'
        && typeof sourceWidth === 'number'
        && typeof sourceHeight === 'number'
    ) {
        context.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            offsetX - drawWidth / 2,
            offsetY - drawHeight / 2,
            drawWidth,
            drawHeight,
        );
    } else {
        context.drawImage(image, offsetX - drawWidth / 2, offsetY - drawHeight / 2, drawWidth, drawHeight);
    }
    context.restore();
}
